import type { Database } from '@shkola/db-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';

export type MeetingFormat = Database['public']['Enums']['meeting_format'];

interface Person {
  first_name: string;
  last_name: string;
}
const fullName = (p: Person | null) => (p ? `${p.first_name} ${p.last_name}` : null);

export interface MeetingSlot {
  id: string;
  startsAt: string;
  endsAt: string;
  format: MeetingFormat;
  place: string | null;
  topic: string | null;
  /** the parent who booked it; null while free */
  guestName: string | null;
  studentName: string | null;
  hostName: string | null;
}

const SLOT_SELECT = `
  id, starts_at, ends_at, format, place, topic,
  guest:school_members!meeting_slots_school_id_guest_member_id_fkey(first_name, last_name),
  student:school_members!meeting_slots_school_id_student_member_id_fkey(first_name, last_name),
  host:school_members!meeting_slots_school_id_host_member_id_fkey(first_name, last_name)
`;

function toSlot(r: {
  id: string;
  starts_at: string;
  ends_at: string;
  format: MeetingFormat;
  place: string | null;
  topic: string | null;
  guest: Person | null;
  student: Person | null;
  host: Person | null;
}): MeetingSlot {
  return {
    id: r.id,
    startsAt: r.starts_at,
    endsAt: r.ends_at,
    format: r.format,
    place: r.place,
    topic: r.topic,
    guestName: fullName(r.guest),
    studentName: fullName(r.student),
    hostName: fullName(r.host),
  };
}

/** From the start of today: the slots a staff member receives in, or a parent's bookings. */
export function useMeetings(filter: { hostId: string } | { guestId: string }) {
  const db = useDb();
  return useQuery({
    queryKey: ['meetings', filter],
    refetchInterval: 60_000,
    queryFn: async () => {
      const since = new Date();
      since.setHours(0, 0, 0, 0);
      let query = db.from('meeting_slots').select(SLOT_SELECT).gte('ends_at', since.toISOString());
      query =
        'hostId' in filter
          ? query.eq('host_member_id', filter.hostId)
          : query.eq('guest_member_id', filter.guestId);
      return must(await query.order('starts_at').limit(200)).map(toSlot);
    },
  });
}

/** Families: whom they can meet about this child, and how many free slots each has. */
export function useMeetingHosts(studentId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['meetings', 'hosts', studentId],
    queryFn: async () => must(await db.rpc('meeting_hosts', { p_student: studentId })),
  });
}

/** Families: the free slots of one person (no names of other families). */
export function useAvailableSlots(hostId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['meetings', 'free', hostId],
    enabled: hostId !== undefined,
    queryFn: async () =>
      must(await db.rpc('available_meeting_slots', { p_host: hostId as string })),
  });
}

/** An online meeting opens 10 minutes before the start and stays open 30 minutes after. */
export function canJoinMeeting(
  slot: { format: string; guestName: string | null; startsAt: string; endsAt: string },
  now = Date.now(),
) {
  return (
    slot.format === 'online' &&
    slot.guestName !== null &&
    now >= Date.parse(slot.startsAt) - 10 * 60_000 &&
    now <= Date.parse(slot.endsAt) + 30 * 60_000
  );
}

export function useMeetingActions() {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => client.invalidateQueries({ queryKey: ['meetings'] });
  return {
    createSlots: useMutation({
      mutationFn: async (a: {
        schoolId: string;
        date: string;
        from: string;
        to: string;
        minutes: number;
        format: MeetingFormat;
        place: string;
      }) =>
        must(
          await db.rpc('create_meeting_slots', {
            p_school: a.schoolId,
            p_date: a.date,
            p_from: a.from,
            p_to: a.to,
            p_minutes: a.minutes,
            p_format: a.format,
            p_place: a.place,
          }),
        ),
      onSettled: invalidate,
    }),
    book: useMutation({
      mutationFn: async (a: { slotId: string; studentId: string; topic: string }) =>
        must(
          await db.rpc('book_meeting', {
            p_slot: a.slotId,
            p_student: a.studentId,
            p_topic: a.topic,
          }),
        ),
      onSettled: invalidate,
    }),
    cancel: useMutation({
      mutationFn: async (slotId: string) =>
        must(await db.rpc('cancel_meeting', { p_slot: slotId })),
      onSettled: invalidate,
    }),
    /** Demo stacks only: an online meeting with the demo mother, right now. */
    startDemo: useMutation({
      mutationFn: async () => must(await db.rpc('demo_start_meeting_now')),
      onSettled: invalidate,
    }),
  };
}
